// Network client for the chat-persistence backend surface.
//
// Two transports, mirroring the backend split:
//   • gRPC-gateway RPCs (reads, delete, and the gzip boundary write SyncChat at
//     POST /v1/chats:sync) — JSON over restPost/restGet/authedFetch; the `chat_id`
//     proto `bytes` field rides as BASE64. (Blob ids never appear as a bytes
//     field here: on the sync body they're opaque JSON inside `blob_refs_json`.)
//   • plain HTTP blob routes — the single-PUT upload and streamed read of
//     /v1/chat-blobs — take a HYPHENATED UUID in the query string (serde `Uuid`)
//     for both chat_id and blob_id.
// The asymmetry is deliberate: do NOT send an RPC's base64 chat_id on the blob
// routes or vice-versa.
//
// The bearer + 401-refresh + dev-proxy routing are handled by authFetchShim /
// restClient; callers here never touch Authorization.

import { authedFetch, restGet, restPost } from "./restClient.js";
import { chatIdToWireBytes, wireBytesToChatId } from "./chatServerId.js";

// A sync attempt's typed outcome, so the engine can branch without parsing HTTP
// at the call site. The `terminal` getter distinguishes a transient failure
// (retry at the next boundary) from one a retry won't fix — 403 forbidden, 404
// not_found, and the two 400s (deleted / invalid) the transcoded RPC returns.
export class ChatSyncError extends Error {
    constructor(kind, { status, currentRevision, expectedRevision, message } = {}) {
        super(message || kind);
        this.name = "ChatSyncError";
        this.kind = kind; // "conflict" | "forbidden" | "deleted" | "not_found" | "invalid" | "quota" | "busy" | "network"
        this.status = status ?? null;
        this.currentRevision = currentRevision ?? null;
        this.expectedRevision = expectedRevision ?? null;
    }

    // 4xx that a retry won't fix — the engine must not hot-loop these.
    // "precondition" is the raw 400 the engine reloads GetChat to reclassify
    // into deleted (drop local) or invalid (park); either way, not retriable.
    get terminal() {
        return this.kind === "forbidden" || this.kind === "deleted"
            || this.kind === "not_found" || this.kind === "invalid"
            || this.kind === "precondition" || this.kind === "quota";
    }
}

// gzip a UTF-8 string via CompressionStream where available; returns
// { body, encoding } so the caller sets Content-Encoding only when compressed.
async function gzipJson(jsonText) {
    if (typeof CompressionStream === "undefined" || typeof Response === "undefined") {
        return { body: jsonText, encoding: null };
    }
    try {
        const stream = new Response(jsonText).body.pipeThrough(new CompressionStream("gzip"));
        const buf = await new Response(stream).arrayBuffer();
        return { body: buf, encoding: "gzip" };
    } catch {
        // Any environment quirk → fall back to plain JSON; the route accepts an
        // uncompressed body just as well (it only decompresses when the header
        // is present).
        return { body: jsonText, encoding: null };
    }
}

// POST one boundary sync. `body` is the fully-assembled SyncChat request (proto
// JSON: base64 `chat_id`, flat head fields, `turns[].payload_json`, …). The
// route is the transcoded `SyncChat` RPC behind a gzip-inflating gateway shim.
// Resolves to the normalized ack { revision, turn_count, truncated_before_ordinal?,
// … } (proto-JSON is camelCase on the wire) or throws a ChatSyncError.
export async function postChatSync(body) {
    const jsonText = JSON.stringify(body);
    const { body: payload, encoding } = await gzipJson(jsonText);
    const headers = { "Content-Type": "application/json" };
    if (encoding) headers["Content-Encoding"] = encoding;

    let res;
    try {
        res = await authedFetch("/v1/chats:sync", { method: "POST", headers, body: payload });
    } catch (err) {
        throw new ChatSyncError("network", { message: String(err?.message || err) });
    }

    let parsed = null;
    try { parsed = await res.json(); } catch { /* non-JSON body */ }
    if (res.ok) return mapSyncAck(parsed);

    // Transcoded gRPC error envelope: google.rpc.Status { code, message, … }.
    // We deliberately do NOT parse the free-text message to recover structured
    // facts — the sync route emits no structured google.rpc error details, so
    // the message wording is not a contract. The engine reloads (GetChat /
    // ListChatTurns) to reconcile a 409 and to classify a 400.
    const message = parsed?.message || res.statusText;
    switch (res.status) {
        // Aborted (revision CAS loss). Don't extract revisions from the message —
        // the engine reloads and reconciles against the returned revision.
        case 409: throw new ChatSyncError("conflict", { status: 409, message });
        case 403: throw new ChatSyncError("forbidden", { status: 403, message });
        case 404: throw new ChatSyncError("not_found", { status: 404, message });
        // FailedPrecondition (a tombstoned chat) and InvalidArgument (a malformed
        // body) BOTH transcode to 400 and there's no structured discriminator, so
        // don't guess from the message. The engine reloads GetChat and keys on the
        // deletedAt tombstone to tell a real delete from a bad request.
        case 400: throw new ChatSyncError("precondition", { status: 400, message });
        // Body over the gateway's decompressed-size limit. Re-POSTing the same
        // body would 413 again, so park it terminal ("invalid") rather than
        // hot-looping every boundary; a later edit that shrinks the chat clears
        // the park (markDirty lifts a non-deleted terminal) and retries.
        case 413: throw new ChatSyncError("invalid", { status: 413, message });
        // On the SyncChat RPC, resource_exhausted (429) is ONLY the per-user
        // chat-count quota (there's no rate-limiter shed on this route — the
        // concurrency semaphore queues). It won't self-heal by retrying, so park
        // it terminal + surface a "not backing up" state rather than looping
        // every boundary. (Transient 429s live on the blob-PUT route, handled by
        // putChatBlob's isRateLimited defer.)
        case 429: throw new ChatSyncError("quota", { status: 429, message });
        case 503: throw new ChatSyncError("busy", { status: 503, message }); // limiter/service transient — retry

        default: throw new ChatSyncError("network", { status: res.status, message });
    }
}

// Normalize the proto-JSON (camelCase) SyncChat ack to the engine's snake_case
// shape, tolerating either casing.
function mapSyncAck(json) {
    return {
        revision: Number(json?.revision ?? 0),
        turn_count: Number(json?.turnCount ?? json?.turn_count ?? 0),
        truncated_before_ordinal: json?.truncatedBeforeOrdinal ?? json?.truncated_before_ordinal ?? null,
        turn_bytes: Number(json?.turnBytes ?? json?.turn_bytes ?? 0),
        blob_bytes: Number(json?.blobBytes ?? json?.blob_bytes ?? 0),
    };
}

// ── Reads (gRPC RPCs; ids are base64 bytes) ───────────────────────────

// List the tab-strip projection. `includeDeleted` surfaces tombstones so a
// fresh device can drop a locally-cached chat that was deleted elsewhere.
export async function listChats({ pageSize = 100, pageToken = "", includeDeleted = false } = {}) {
    const params = { page_size: pageSize };
    if (pageToken) params.page_token = pageToken;
    if (includeDeleted) params.include_deleted = true;
    const json = await restGet("/v1/chats", params);
    const rows = json?.chats ?? [];
    return {
        chats: rows.map(mapChatSummary),
        nextPageToken: json?.next_page_token ?? json?.nextPageToken ?? "",
    };
}

export async function getChat(chatId) {
    const json = await restPost("/v1/chats:get", { chat_id: chatIdToWireBytes(chatId) });
    return mapChat(json?.chat ?? null);
}

// Newest-first page of turns; `beforeOrdinal` scrolls back.
export async function listChatTurns(chatId, { beforeOrdinal = null, pageSize = 50 } = {}) {
    const body = { chat_id: chatIdToWireBytes(chatId), page_size: pageSize };
    if (beforeOrdinal != null) body.before_ordinal = beforeOrdinal;
    const json = await restPost("/v1/chats:list-turns", body);
    const rows = json?.turns ?? [];
    return rows.map(mapChatTurn);
}

// Tombstone the chat everywhere (other devices drop their local copy).
export async function deleteChat(chatId) {
    await restPost("/v1/chats:delete", { chat_id: chatIdToWireBytes(chatId) });
}

// What the model last saw on this chat: the newest invocation's input manifest,
// resolved to its ordered normalized turns. Capture is asynchronous and
// fail-open, so an empty `turns` with `invocationFound: false` is the ordinary
// "nothing published yet" answer, NOT an error - the caller reconstructs from
// the persisted transcript instead.
export async function getChatContinuation(chatId) {
    const json = await restPost("/v1/chats:continuation", { chat_id: chatIdToWireBytes(chatId) });
    return {
        turns: (json?.turns ?? []).map((turn) => ({
            ordinal: Number(turn.ordinal ?? 0),
            role: turn.role ?? "",
            usage: turn.usage ?? "",
            // The body stays a STRING here; chatContinuation parses it once, in
            // the one place that knows the normalized-turn shape.
            bodyJson: turn.body_json ?? turn.bodyJson ?? "",
        })),
        invocationFound: Boolean(json?.invocation_found ?? json?.invocationFound),
        model: json?.model ?? "",
        invocationAt: json?.invocation_at ?? json?.invocationAt ?? null,
        truncated: Boolean(json?.truncated),
        droppedTurnCount: Number(json?.dropped_turn_count ?? json?.droppedTurnCount ?? 0),
        droppedByteCount: Number(json?.dropped_byte_count ?? json?.droppedByteCount ?? 0),
    };
}

// ── Blobs (single authenticated PUT; bytes live in Postgres) ───────────

// Upload one blob's bytes in a single PUT. The client mints `blobId` (a UUID);
// blob bytes are stored inline in Postgres (no presign, no S3). Query params are
// HYPHENATED UUID strings — this is a plain axum route (serde `Uuid`), NOT the
// base64 `bytes` form the gRPC RPCs use; do not reuse an RPC's base64 id here.
// Returns { sizeBytes, replayed, evictedBlobIds } — evicted ids are hyphenated
// UUID strings the engine drops from its dedup cache. Throws an Error carrying
// `.status` on an HTTP error so the caller can detect a 429 rate limit.
export async function putChatBlob({ chatId, blobId, turnOrdinal, contentType, bytes }) {
    const type = contentType || "application/octet-stream";
    const url = `/v1/chat-blobs?chat_id=${encodeURIComponent(chatId)}&blob_id=${encodeURIComponent(blobId)}`
        + `&turn_ordinal=${turnOrdinal}&content_type=${encodeURIComponent(type)}`;
    const res = await authedFetch(url, { method: "PUT", headers: { "Content-Type": type }, body: bytes });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        const err = new Error(`chat blob PUT ${res.status}: ${text || res.statusText}`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json().catch(() => ({}));
    return {
        sizeBytes: Number(json?.size_bytes ?? json?.sizeBytes ?? 0),
        replayed: Boolean(json?.replayed),
        // Hyphenated UUID strings (Uuid::to_string) — no base64 decode needed.
        evictedBlobIds: json?.evicted_blob_ids ?? json?.evictedBlobIds ?? [],
    };
}

// Whether an error from the blob-upload path is the per-(tenant,user) rate
// limit (HTTP 429) — the engine batches mints and tolerates this by deferring
// the rest of the batch to the next boundary (outbox), never hot-retrying.
export function isRateLimited(err) {
    return err?.status === 429;
}

// Streamed authenticated read of one blob. Hyphenated UUIDs in the query
// string (the blob route takes serde `Uuid`, not base64). Returns the decoded
// content as a string (text) or a data URL (binary) so hydrateBlobPlaceholders
// can drop it straight back into the render document.
export async function readBlob(chatId, blobId, { contentType } = {}) {
    const url = `/v1/chat-blobs?chat_id=${encodeURIComponent(chatId)}&blob_id=${encodeURIComponent(blobId)}`;
    const res = await authedFetch(url);
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`chat blob read ${res.status}: ${text || res.statusText}`);
    }
    const type = res.headers.get("content-type") || contentType || "application/octet-stream";
    if (type.startsWith("text/") || type.includes("json")) {
        return res.text();
    }
    const buf = await res.arrayBuffer();
    let binary = "";
    const view = new Uint8Array(buf);
    for (let i = 0; i < view.length; i++) binary += String.fromCharCode(view[i]);
    return `data:${type};base64,${btoa(binary)}`;
}

// ── response mappers ──────────────────────────────────────────────────

function mapChatSummary(row) {
    return {
        chatId: wireBytesToChatId(row.chat_id ?? row.chatId),
        title: row.title ?? "",
        kind: row.kind || null,
        turnCount: Number(row.turn_count ?? row.turnCount ?? 0),
        revision: Number(row.revision ?? 0),
        interrupted: Boolean(row.interrupted),
        updatedAt: row.updated_at ?? row.updatedAt ?? null,
        deletedAt: row.deleted_at ?? row.deletedAt ?? null,
    };
}

function mapChat(chat) {
    if (!chat) return null;
    return {
        chatId: wireBytesToChatId(chat.chat_id ?? chat.chatId),
        title: chat.title ?? "",
        kind: chat.kind || null,
        thinkingLevel: (chat.thinking_level ?? chat.thinkingLevel) || null,
        scopeMode: chat.scope_mode ?? chat.scopeMode ?? null,
        canvas: parseJsonField(chat.canvas_json ?? chat.canvasJson),
        itemUiState: parseJsonField(chat.item_ui_state_json ?? chat.itemUiStateJson),
        turnCount: Number(chat.turn_count ?? chat.turnCount ?? 0),
        revision: Number(chat.revision ?? 0),
        interrupted: Boolean(chat.interrupted),
        truncatedBeforeOrdinal: chat.truncated_before_ordinal ?? chat.truncatedBeforeOrdinal ?? null,
        conversationRefs: parseJsonField(chat.conversation_refs_json ?? chat.conversationRefsJson),
        updatedAt: chat.updated_at ?? chat.updatedAt ?? null,
        deletedAt: chat.deleted_at ?? chat.deletedAt ?? null,
    };
}

function mapChatTurn(turn) {
    return {
        turn_ordinal: Number(turn.turn_ordinal ?? turn.turnOrdinal ?? 0),
        payload: parseJsonField(turn.payload_json ?? turn.payloadJson) ?? {},
        blobRefs: parseJsonField(turn.blob_refs_json ?? turn.blobRefsJson) ?? [],
    };
}

function parseJsonField(value) {
    if (value == null || value === "") return null;
    if (typeof value === "object") return value;
    try { return JSON.parse(value); } catch { return null; }
}
